import { Skeleton } from '@app/components/various/Skeleton';
import { INPUT_MAX_LENGTH } from '@app/constants/limits';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { Input as InputNs } from '@typings';
import React from 'react';
import { mergeRefs } from 'react-laag';

import { InputCopyButton } from './InputCopyButton';

export const Input = React.forwardRef<HTMLInputElement, InputNs.Props>((props, ref) => {
  const {
    className,
    copyButtonRef,
    dataTestId,
    describedById,
    errorMessageId,
    isCopyable = false,
    isDisabled,
    isInvalid = false,
    isLoading = false,
    isReadonly = false,
    isRequired = false,
    icon: Icon,
    postfix,
    size = 'large',
    type = 'text',
    value,
    onCopy,
    inputClassName,
    ...rest
  } = props;

  const internalRef = React.useRef<HTMLInputElement | null>(null);

  if (isLoading) {
    return <Skeleton type="input" size={size} />;
  }

  const isSizeMedium = size === 'medium';

  return (
    <div
      className={cn(
        'relative flex h-10 w-full cursor-text items-center rounded-regular border border-neutral-90 px-3 py-2 transition-colors',
        'hover:border-neutral-60 [&:has(:hover)]:border-neutral-60 [&:has(input:focus)]:border-neutral-60 [&:has(input:focus)]:focus-input-outline',
        '[&:has(:disabled)]:cursor-not-allowed [&:has(:disabled)]:!border-neutral-90 [&:has(:disabled)]:bg-neutral-96',
        {
          'border-error-45 [&:has(:hover)]:border-error-45 [&:has(input:focus)]:!outline-error-45': isInvalid,
          'border-neutral-96 bg-neutral-96 [&:has(:hover)]:border-neutral-96': isReadonly,
          'cursor-not-allowed border-neutral-90 bg-neutral-96 hover:border-neutral-90': isDisabled,
          'h-8 py-1': isSizeMedium,
        },
        className,
      )}
    >
      <input
        ref={mergeRefs(internalRef, ref)}
        className={cn(
          'w-full text-neutral-20 outline-none transition-colors body-2 placeholder:text-neutral-60',
          'bg-transparent disabled:cursor-not-allowed disabled:text-neutral-80 disabled:placeholder:text-neutral-80',
          {
            'body-4': isSizeMedium,
          },
          inputClassName,
        )}
        type={type}
        aria-invalid={isInvalid}
        aria-describedby={describedById}
        aria-errormessage={errorMessageId}
        maxLength={INPUT_MAX_LENGTH}
        disabled={isDisabled}
        readOnly={isReadonly}
        data-testid={dataTestId}
        required={isRequired}
        value={value}
        {...rest}
      />
      {isDefined(Icon) && <Icon />}
      {isDefined(postfix) && (
        <div
          className={cn('ml-0.5 body-2', {
            'body-4': isSizeMedium,
          })}
        >
          {postfix}
        </div>
      )}
      {isCopyable && <InputCopyButton ref={copyButtonRef} inputRef={internalRef} onClick={onCopy} />}
    </div>
  );
});
